// Lista de White Labels (§6.1): busca (client-side sobre a página
// carregada + filtro server-side por nome no submit), grid
// responsivo de cards e paginação. Estados: skeleton (grid de 6),
// erro com retry e vazio.
import { PlusIcon, SearchIcon } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import { useAdminWhiteLabels } from "@/services/admin"

import { filterWhiteLabels } from "../utils"
import { CreateWhiteLabelDialog } from "./create-white-label-dialog"
import { WhiteLabelCard } from "./white-label-card"

const panelPrefix = "pages.adminWhiteLabels"

// Tamanho da página do grid (1-3 colunas → múltiplo de 6).
const PAGE_SIZE = 12

// Skeleton do grid (6 cards).
const ListSkeleton = () => (
  <div
    data-testid="white-labels-skeleton"
    className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3"
  >
    {Array.from({ length: 6 }).map((_, index) => (
      <Skeleton
        key={`wl-skeleton-${index}`}
        className="h-40 w-full rounded-xl"
      />
    ))}
  </div>
)

export const WhiteLabelsList = () => {
  const { t } = useTranslation()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  // Texto do input (refina client-side na página carregada).
  const [search, setSearch] = useState("")
  // Filtro server-side aplicado no submit (Enter).
  const [submittedName, setSubmittedName] = useState("")
  // Paginação 1-BASED (legado do backoffice).
  const [page, setPage] = useState(1)

  const { data, isLoading, isError, refetch } =
    useAdminWhiteLabels({
      name: submittedName || undefined,
      page,
      quantity: PAGE_SIZE,
    })

  // Shape real do backoffice: itens em `whiteLabels` + `count` total.
  const items = useMemo(
    () => data?.whiteLabels ?? [],
    [data?.whiteLabels]
  )
  const filteredItems = useMemo(
    () => filterWhiteLabels(items, search),
    [items, search]
  )

  // Última página: usa o `count` quando o backend informa; senão,
  // deduz pela página cheia.
  const isLastPage =
    data?.count !== undefined
      ? page * PAGE_SIZE >= data.count
      : items.length < PAGE_SIZE

  const handleSubmitSearch = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault()
    setSubmittedName(search.trim())
    setPage(1)
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <form
          onSubmit={handleSubmitSearch}
          className="relative w-full @2xl:max-w-sm"
        >
          <SearchIcon
            className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            className="pl-8"
            placeholder={t(
              `${panelPrefix}.searchPlaceholder`
            )}
            aria-label={t(
              `${panelPrefix}.searchPlaceholder`
            )}
            value={search}
            onChange={(event) =>
              setSearch(event.target.value)
            }
          />
        </form>
        <Button
          type="button"
          onClick={() => setIsCreateOpen(true)}
        >
          <PlusIcon
            className="mr-1 size-4"
            aria-hidden="true"
          />
          {t(`${panelPrefix}.create.button`)}
        </Button>
      </div>

      <CreateWhiteLabelDialog
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
      />

      {isLoading && <ListSkeleton />}

      {!isLoading && isError && (
        <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
          <p className="text-center text-sm text-red-700">
            {t(`${panelPrefix}.loadError`)}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => refetch()}
          >
            {t(`${panelPrefix}.retry`)}
          </Button>
        </div>
      )}

      {!isLoading && !isError && (
        <>
          {filteredItems.length === 0 && (
            <div
              data-testid="white-labels-empty"
              className="flex flex-col items-center gap-2 rounded-xl border border-dashed px-6 py-14 text-center"
            >
              <p className="text-sm text-muted-foreground">
                {t(`${panelPrefix}.emptyState`)}
              </p>
            </div>
          )}

          {filteredItems.length > 0 && (
            <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">
              {filteredItems.map((whiteLabel) => (
                <WhiteLabelCard
                  key={whiteLabel.uuid}
                  whiteLabel={whiteLabel}
                />
              ))}
            </div>
          )}

          <div className="flex items-center justify-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() =>
                setPage((prev) => Math.max(prev - 1, 1))
              }
            >
              {t(`${panelPrefix}.pagination.previous`)}
            </Button>
            <span className="text-sm tabular-nums text-muted-foreground">
              {t(`${panelPrefix}.pagination.page`, {
                page,
              })}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isLastPage}
              onClick={() => setPage((prev) => prev + 1)}
            >
              {t(`${panelPrefix}.pagination.next`)}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
